import type { ArticulatedNote } from "./types";
import type { Instrument, InstrumentVariant } from "../data/instruments";
import type { AudioVoice } from "./constants";
import {
  applyPitchBend,
  clampSitarMeend,
  erhuPortamentoLogistic,
  erhuPortamentoSmoothstep,
  gayageumDecayWithNonghyeon,
  gayageumNonghyeonCurve,
  tablaBayanPressureBend
} from "./pitch";

export interface PhysicalModelOptions {
  controlValue?: number;
  meendMode?: "beginner" | "standard" | "expert";
  erhuPortamentoShape?: "smoothstep" | "logistic";
}

export class PhysicalModelLayer {
  apply(
    context: BaseAudioContext,
    voice: AudioVoice,
    note: ArticulatedNote,
    instrument: Instrument,
    variant: InstrumentVariant,
    options: PhysicalModelOptions = {}
  ): void {
    for (const source of voice.sources) {
      if (!(source instanceof OscillatorNode)) {
        continue;
      }

      if (instrument.id === "gayageum" && note.ornament === "nonghyeon") {
        const depth = 36 + (options.controlValue ?? 0.5) * 92;
        const adjustedEnvelope = gayageumDecayWithNonghyeon(note.decay, note.release, depth);
        const bendPoints = Array.from({ length: 8 }, (_, index) => {
          const time = index * 0.055;

          return {
            time,
            cents: gayageumNonghyeonCurve(0, depth, time)
          };
        });

        applyPitchBend(source.detune, context, bendPoints);
        note.decay = adjustedEnvelope.decay;
        note.release = adjustedEnvelope.release;
      }

      if (instrument.id === "gayageum" && note.ornament === "toeseong") {
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.16, cents: -22 },
          { time: 0.32, cents: -86 }
        ]);
      }

      if (instrument.id === "gayageum" && note.ornament === "jeonseong") {
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.035, cents: 36 },
          { time: 0.07, cents: -8 },
          { time: 0.11, cents: 0 }
        ]);
      }

      if (instrument.id === "oud" && note.ornament === "micro_bend") {
        const bend = 8 + (options.controlValue ?? 0.35) * 42;
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.12, cents: bend },
          { time: 0.24, cents: 0 }
        ]);
      }

      if (instrument.id === "ney" && note.ornament === "micro_bend") {
        const bend = 8 + (options.controlValue ?? 0.35) * 48;
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.16, cents: bend },
          { time: 0.3, cents: -bend * 0.3 },
          { time: 0.44, cents: 0 }
        ]);
      }

      if (instrument.id === "daegeum" && note.ornament === "yoseong") {
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.18, cents: 18 },
          { time: 0.36, cents: -10 },
          { time: 0.54, cents: 16 },
          { time: 0.72, cents: 0 }
        ]);
      }

      if (instrument.id === "daegeum" && note.ornament === "toeseong") {
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.18, cents: -30 },
          { time: 0.42, cents: -92 }
        ]);
      }

      if (instrument.id === "erhu" && note.ornament === "portamento") {
        const endHz =
          options.erhuPortamentoShape === "logistic"
            ? erhuPortamentoLogistic(note.frequency, note.frequency * 1.12246, 1)
            : erhuPortamentoSmoothstep(note.frequency, note.frequency * 1.12246, 1);
        source.frequency.linearRampToValueAtTime(endHz, context.currentTime + 0.34);
      }

      if (instrument.id === "sitar" && note.ornament === "meend") {
        const cents = clampSitarMeend(options.controlValue ?? 500, options.meendMode ?? "standard");
        applyPitchBend(source.detune, context, [
          { time: 0, cents: 0 },
          { time: 0.45, cents },
          { time: 0.68, cents: cents * 0.72 }
        ]);
      }

      if (instrument.id === "tabla" && variant.id.includes("tabla") && note.ornament === "bayan_pressure") {
        const pressureHz = tablaBayanPressureBend(note.frequency, options.controlValue ?? 0.5);
        source.frequency.linearRampToValueAtTime(pressureHz, context.currentTime + 0.18);
      }
    }
  }
}
